import type {
  DownloadedFile,
  PlanExportFormat,
  PlanPaperSize,
  NewStockCatalogueItem,
  StockAdjustmentsPatch,
  StockCatalogueItem,
  StockCatalogueItemPatch,
  SurveyStock,
} from "./types.js";
import type { DrawnPlan } from "@sts/geometry";
import type {
  AssignJobInput,
  Assumption,
  BusDeadlinesQuery,
  BusDeadlinesResponse,
  BusEligibilityRequest,
  BusEligibilityResponse,
  CaptureSession,
  ChatMessage,
  ChatThread,
  ChatThreadSummary,
  Colleague,
  CompletedPasswordReset,
  ConfirmUValueInput,
  ConfirmUValueResult,
  Contact,
  RoomDetailsInput,
  RoomDetailsResult,
  CreateContactInput,
  CreateEnvelopeInput,
  CreateJobInput,
  CreatePropertyInput,
  CreateTaskInput,
  Deadline,
  DesignDecisionsPatch,
  DesignReadiness,
  AgreementLinkView,
  SupplyBill,
  AwaitingCountersignature,
  ExportTokenView,
  HandoffTokenView,
  SiteSurveyAnswersBySection,
  SiteSurveySectionAnswers,
  DesignConditionsView,
  DefaultMaterialRow,
  RoomNotesView,
  PlanImageView,
  SurveyPlanOrientation,
  DesignReadinessWrite,
  StoreyCeilingAnswer,
  StoreyFloorAnswer,
  Construction,
  ConstructionElement,
  DesignCheckResponse,
  DesignDecisionsResponse,
  DocumentContent,
  DocumentIntegrity,
  DocumentSearchQuery,
  DocumentSearchResult,
  DocumentSend,
  SavedSurveyDrawing,
  SurveyDrawingState,
  DocumentSummary,
  Health,
  InstallationsResponse,
  Invitation,
  InviteMemberInput,
  Job,
  JobCustomer,
  JobCustomerPatch,
  JobSurveySummary,
  ListedDocument,
  LoginResult,
  LogoutResult,
  Me,
  MeasureCategory,
  Member,
  MemberPasswordReset,
  NewPlanComment,
  NewPlanDimension,
  NewPlanObject,
  Notification,
  PlanCommentPatch,
  PlanCommentThread,
  PlanDimensionPatch,
  PlanDimensionRecord,
  PlanEditBatch,
  PlanEditList,
  NotificationQuery,
  OpenInvitation,
  OpenPasswordReset,
  OverridePositionInput,
  PasswordResetRequested,
  PhotoEvidence,
  SurveyMeasurement,
  PhotoIntegrity,
  PlanObject,
  PlanObjectPatch,
  PostMessageInput,
  Property,
  RequiredPhotosForMeasure,
  RetrofitMeasurePackage,
  RoomEmitterInput,
  RoomEmitterDecision,
  RetrofitProjectDetail,
  RetrofitProjectListItem,
  RetrofitReadiness,
  SendDocumentInput,
  JobContract,
  OrganisationProfile,
  SendDocumentResult,
  SigningLinkResult,
  SentInvitation,
  SignatureEnvelope,
  SignatureVerification,
  SpaceModel,
  SurveyHeatLoss,
  SurveyPropertyDetails,
  SurveyPropertyInput,
  SurveyScan,
  ScanCalibrationList,
  NewScanCalibration,
  RecordedScanCalibration,
  WithdrawnScanCalibration,
  SurveySummary,
  SurveyListed,
  SurveyOverview,
  Task,
  RecycleItem,
  TaskCounts,
  ThreadReadResult,
  TransitionInput,
  UnreadSummary,
  UpdateMemberInput,
  UpdateTaskInput,
  WallAdjacencyInput,
  WallAdjacencyResult,
} from "./types.js";
import type {
  TrustmarkAmendLodgementBody,
  TrustmarkAssessmentSubmitBody,
  TrustmarkCompleteProjectBody,
  TrustmarkMeasureLodgeBody,
  TrustmarkPropertyInformationUpdate,
  TrustmarkStartProjectBody,
} from "./retrofit-types.js";
import type {
  CreatedSurveyShareLink,
  JobsMapResponse,
  NewSurveyShareLink,
  ShareLinkView,
  SurveyShareLink,
} from "./share-map-types.js";

/**
 * The typed boundary between the portal and the API.
 *
 * Every method names the route it calls and returns the shape that route
 * returns. Nothing here is `any`, and nothing outside this file touches
 * `fetch` — so when the API changes, exactly one file has to change with it.
 */

/** The subset of `fetch` this client uses, so tests can supply the real app. */
export type FetchLike = (
  input: string,
  init?: RequestInit,
) => Promise<Response>;

export interface Credentials {
  readonly token: string;
  /**
   * Sent as `X-Organisation-Id`. Required only when the user belongs to more
   * than one organisation: `app_resolve_session` refuses to pick for them,
   * because guessing would mean serving somebody another tenant's records.
   */
  readonly organisationId: string | null;
}

/**
 * A structured failure from the API.
 *
 * The API answers every error with `{ error, message, details? }` and takes
 * care over the wording — "A job cannot move from signed to certified", not
 * "Bad Request". Throwing that away and showing "Something went wrong" would
 * discard the most useful thing in the response.
 */
/**
 * `?reason=...` for a deletion, or nothing at all.
 *
 * A query parameter rather than a body because these are DELETE requests,
 * and a DELETE carrying a body is read differently by enough clients and
 * proxies not to be worth the argument.
 */
function reasonQuery(reason?: string): string {
  const trimmed = reason?.trim();
  return trimmed ? `?reason=${encodeURIComponent(trimmed)}` : "";
}

export class ApiError extends Error {
  readonly status: number;
  /** The API's machine-readable code, e.g. `forbidden`, `invalid_transition`. */
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** A network failure or a response that was not JSON at all. */
export class ApiUnreachableError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "ApiUnreachableError";
  }
}

/**
 * `forbidden` responses carry `{ resource, action }`, which is what the
 * permission matrix actually refused. Saying "you cannot sign off a design"
 * beats "access denied" — it tells the reader whether to ask someone else or to
 * give up.
 */
export function refusedAction(
  error: ApiError,
): { resource: string; action: string } | null {
  const details = error.details;
  if (!details) return null;
  const resource = details["resource"];
  const action = details["action"];
  if (typeof resource !== "string" || typeof action !== "string") return null;
  return { resource, action };
}

/** `compliance_failure` responses carry the rules that were breached. */
export interface ComplianceFailure {
  readonly code: string;
  readonly message: string;
  readonly severity: string;
  readonly subjectId?: string;
}

export function complianceFailures(error: ApiError): ComplianceFailure[] {
  const failures = error.details?.["failures"];
  if (!Array.isArray(failures)) return [];
  return failures.filter(
    (f): f is ComplianceFailure =>
      typeof f === "object" &&
      f !== null &&
      typeof (f as { code?: unknown }).code === "string" &&
      typeof (f as { message?: unknown }).message === "string",
  );
}

export interface ApiClientOptions {
  /** Empty in the browser (the dev server proxies /api and /auth). */
  readonly baseUrl?: string;
  readonly fetch?: FetchLike;
  /** Read fresh on every request so a sign-out takes effect immediately. */
  readonly credentials?: () => Credentials | null;
  /** Called before the 401 is thrown, so the app can send the user to sign in. */
  readonly onUnauthorised?: (error: ApiError) => void;
}

type QueryValue = string | number | boolean | undefined;

/** What each plan export is, for the request's `accept` header. */
const PLAN_EXPORT_ACCEPT: Readonly<Record<PlanExportFormat, string>> = {
  pdf: "application/pdf",
  png: "image/png",
  svg: "image/svg+xml",
  dxf: "application/dxf",
};

export class ApiClient {
  private readonly baseUrl: string;
  private readonly doFetch: FetchLike;
  private readonly credentials: () => Credentials | null;
  private readonly onUnauthorised: ((error: ApiError) => void) | undefined;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? "";
    this.doFetch =
      options.fetch ??
      ((input, init) => globalThis.fetch(input, init as RequestInit));
    this.credentials = options.credentials ?? (() => null);
    this.onUnauthorised = options.onUnauthorised;
  }

  // ----------------------------------------------------------------- health

  health(): Promise<Health> {
    return this.request<Health>("GET", "/health", { anonymous: true });
  }

  // --------------------------------------------------------- authentication

  /**
   * Sign in. Unauthenticated by definition — this is the call that produces the
   * token every other call carries, so it must not be routed through the 401
   * handler that sends the user back to the sign-in screen they are already on.
   */
  login(email: string, password: string): Promise<LoginResult> {
    return this.request<LoginResult>("POST", "/auth/login", {
      anonymous: true,
      body: { email, password },
    });
  }

  logout(): Promise<LogoutResult> {
    return this.request<LogoutResult>("POST", "/auth/logout", {});
  }

  // ------------------------------------------------------------------- me

  /** The caller, as the API resolved them from the token they presented. */
  me(): Promise<Me> {
    return this.request<Me>("GET", "/api/me", {});
  }

  // ------------------------------------------------------------------ jobs

  /**
   * `GET /api/jobs` — this organisation's own jobs, in pipeline order.
   * `{ mine: true }` calls `?mine=true` instead: the same list scoped to the
   * caller, which is what a role holding `job:read` only at "own" scope is
   * refused the bare listing and offered in its place.
   */
  async jobs(opts: { mine?: boolean } = {}): Promise<Job[]> {
    const res = await this.request<{ jobs: Job[] }>("GET", "/api/jobs", {
      query: { mine: opts.mine ? "true" : undefined },
    });
    return res.jobs;
  }

  getJob(id: string): Promise<Job> {
    return this.request<Job>("GET", `/api/jobs/${encodeURIComponent(id)}`, {});
  }

  /**
   * `GET /api/surveys` — every survey, newest first.
   *
   * The survey-only portal's one list. Scoped by the caller's own role on the
   * API side, so this needs no `mine` flag: a surveyor asking gets the surveys
   * of the jobs they are booked onto, an owner gets the organisation's.
   */
  async surveys(): Promise<SurveyListed[]> {
    const res = await this.request<{ surveys: SurveyListed[] }>("GET", "/api/surveys", {});
    return res.surveys;
  }

  /**
   * `GET /api/surveys/:id/overview` — one survey: what it is, what it still
   * needs, and its rooms grouped by the floor each was walked on.
   *
   * One request on purpose. The same figures could be assembled from
   * `/heat-loss`, `/design-readiness`, `/assumptions` and `/jobs/:id/photos`,
   * joined here — and a join on `design-readiness.heatedRooms`, which is
   * optional in the wire type and omits unheated rooms, loses a room the
   * moment somebody marks one unheated.
   */
  surveyOverview(id: string): Promise<SurveyOverview> {
    return this.request<SurveyOverview>(
      "GET",
      `/api/surveys/${encodeURIComponent(id)}/overview`,
      {},
    );
  }

  /**
   * `POST /api/jobs/:id/view` — "I have opened this job", which clears its
   * unread mark on the pipeline board.
   *
   * Separate from `getJob` on purpose. Reading a job and saying you have read
   * it are different claims, and folding the second into the first would mark
   * a job read whenever anything fetched it — a retry, a prefetch, a
   * background refresh — which is precisely when the mark would be wrong.
   */
  markJobViewed(id: string): Promise<{ viewedAt: string }> {
    return this.request<{ viewedAt: string }>(
      "POST",
      `/api/jobs/${encodeURIComponent(id)}/view`,
      {},
    );
  }

  /**
   * Books a surveyor and/or an installer onto a job. `null` clears a field;
   * an omitted field leaves it unchanged — the same convention `updateTask`
   * uses for `assignedToUserId`.
   */
  assignJob(id: string, input: AssignJobInput): Promise<Job> {
    return this.request<Job>(
      "PATCH",
      `/api/jobs/${encodeURIComponent(id)}/assignment`,
      { body: input },
    );
  }

  createJob(input: CreateJobInput): Promise<Job> {
    return this.request<Job>("POST", "/api/jobs", { body: input });
  }

  transitionJob(id: string, input: TransitionInput): Promise<Job> {
    return this.request<Job>(
      "POST",
      `/api/jobs/${encodeURIComponent(id)}/transition`,
      { body: input },
    );
  }

  signOffDesign(id: string): Promise<Job> {
    return this.request<Job>(
      "POST",
      `/api/jobs/${encodeURIComponent(id)}/sign-off`,
      {},
    );
  }

  /**
   * Who the job is for and where it is.
   *
   * Separate from `getJob` because the API keeps it separate, and the API keeps
   * it separate because it is separately gated: a role may read a job without
   * holding the `contact` grant over the customer's name and address.
   */
  jobCustomer(id: string): Promise<JobCustomer> {
    return this.request<JobCustomer>(
      "GET",
      `/api/jobs/${encodeURIComponent(id)}/customer`,
      {},
    );
  }

  /**
   * `PATCH /api/jobs/:id/customer` — record the customer and the address on a
   * job that already exists.
   *
   * The job the FIELD APP opens has neither: it names itself with whatever the
   * surveyor typed and creates no property at all. Until this existed there was
   * no way to give one an address afterwards, which blocked the design, the BUS
   * application and the survey hand-off alike.
   */
  setJobCustomer(id: string, patch: JobCustomerPatch): Promise<JobCustomer> {
    return this.request<JobCustomer>(
      "PATCH",
      `/api/jobs/${encodeURIComponent(id)}/customer`,
      { body: patch },
    );
  }

  async jobTasks(id: string): Promise<Task[]> {
    const res = await this.request<{ tasks: Task[] }>(
      "GET",
      `/api/jobs/${encodeURIComponent(id)}/tasks`,
      {},
    );
    return res.tasks;
  }

  /**
   * `GET /api/jobs/:jobId/surveys` — every capture attached to a job.
   *
   * The counterpart to `POST /api/jobs/:jobId/surveys`. Without it the portal
   * had to remember survey ids per browser (see `KnownRecords`) — which meant a
   * surveyor's upload was invisible on any device but the one that made it.
   */
  /**
   * `POST /api/jobs/:id/merge` — fold another job's field work into this one,
   * for a property a surveyor started a new job for on each floor.
   *
   * Refuses (400) when the other job holds a document, a contract, a BUS
   * voucher or anything else that names it: those cannot be re-attributed,
   * and the message says which one stopped it.
   */
  mergeJobs(
    targetJobId: string,
    sourceJobId: string,
  ): Promise<{
    readonly surveysMoved: number;
    readonly moved: Readonly<Record<string, number>>;
  }> {
    return this.request("POST", `/api/jobs/${encodeURIComponent(targetJobId)}/merge`, {
      body: { sourceJobId },
    });
  }

  /**
   * `POST /api/surveys/:id/merge` — move another survey's rooms onto a floor
   * of this one, so a property surveyed a floor at a time becomes one survey.
   *
   * `planCoversMergedFloor` comes back false: the plan is drawn from one scan
   * per survey, so the floor just added has rooms and heat loss but no plan
   * sheet. The caller is expected to say so.
   */
  mergeSurveys(
    targetSurveyId: string,
    input: { sourceSurveyId: string; level: number; name?: string },
  ): Promise<{
    readonly roomsMoved: number;
    readonly level: number;
    readonly storeyName: string;
    readonly planCoversMergedFloor: boolean;
  }> {
    return this.request(
      "POST",
      `/api/surveys/${encodeURIComponent(targetSurveyId)}/merge`,
      { body: input },
    );
  }

  async jobSurveys(id: string): Promise<JobSurveySummary[]> {
    const res = await this.request<{ surveys: JobSurveySummary[] }>(
      "GET",
      `/api/jobs/${encodeURIComponent(id)}/surveys`,
      {},
    );
    return res.surveys;
  }

  /**
   * `POST /api/jobs/:jobId/surveys` — start a survey from the portal.
   *
   * Every survey used to arrive from the phone, so a job whose property was
   * never visited with the app had no survey and therefore nowhere to put a
   * floor plan, a site survey or a heat loss. This is the way in for a
   * property surveyed on paper, or one whose scan was lost.
   *
   * The external design temperature comes from the caller (the form reads it
   * off MIS 3005-D Table 2); everything else on the survey is editable
   * afterwards through the design conditions card.
   */
  async createSurvey(
    jobId: string,
    input: {
      readonly surveyedOn: string;
      readonly externalDesignTempC: number;
      readonly annualAverageExternalTempC: number;
      readonly externalDesignColumn?: "A" | "B";
    },
  ): Promise<{ id: string; jobId: string }> {
    return this.request<{ id: string; jobId: string }>(
      "POST",
      `/api/jobs/${encodeURIComponent(jobId)}/surveys`,
      { body: input },
    );
  }

  // ------------------------------------------------ contacts and properties

  async contacts(): Promise<Contact[]> {
    const res = await this.request<{ contacts: Contact[] }>(
      "GET",
      "/api/contacts",
      {},
    );
    return res.contacts;
  }

  createContact(input: CreateContactInput): Promise<Contact> {
    return this.request<Contact>("POST", "/api/contacts", { body: input });
  }

  async properties(): Promise<Property[]> {
    const res = await this.request<{ properties: Property[] }>(
      "GET",
      "/api/properties",
      {},
    );
    return res.properties;
  }

  createProperty(input: CreatePropertyInput): Promise<Property> {
    return this.request<Property>("POST", "/api/properties", { body: input });
  }

  // ------------------------------------------------------------- deadlines

  async deadlines(): Promise<Deadline[]> {
    const res = await this.request<{ deadlines: Deadline[] }>(
      "GET",
      "/api/deadlines",
      {},
    );
    return res.deadlines;
  }

  // ----------------------------------------------------------------- tasks

  async tasks(opts: { mine?: boolean } = {}): Promise<Task[]> {
    const res = await this.request<{ tasks: Task[] }>("GET", "/api/tasks", {
      query: { mine: opts.mine ? "true" : undefined },
    });
    return res.tasks;
  }

  taskCounts(): Promise<TaskCounts> {
    return this.request<TaskCounts>("GET", "/api/tasks/counts", {});
  }

  createTask(input: CreateTaskInput): Promise<Task> {
    return this.request<Task>("POST", "/api/tasks", { body: input });
  }

  updateTask(id: string, input: UpdateTaskInput): Promise<Task> {
    return this.request<Task>(
      "PATCH",
      `/api/tasks/${encodeURIComponent(id)}`,
      { body: input },
    );
  }

  // --------------------------------------------------------------- surveys

  getSurvey(id: string): Promise<SurveySummary> {
    return this.request<SurveySummary>(
      "GET",
      `/api/surveys/${encodeURIComponent(id)}`,
      {},
    );
  }

  /**
   * `GET /api/surveys/:id/scan` — the raw RoomPlan capture behind the 3D
   * workspace and the per-element measurement breakdown.
   *
   * `scan` is `null` when the survey has no capture synced yet; the API returns
   * that as a 200, not an error, so the screen can say so plainly rather than
   * treating an un-scanned survey as a failure.
   */
  surveyScan(id: string, opts: { storey?: number } = {}): Promise<SurveyScan> {
    return this.request<SurveyScan>(
      "GET",
      `/api/surveys/${encodeURIComponent(id)}/scan`,
      // A survey combined from two walks holds a capture per floor, each in
      // its own frame (0072). Without a storey the API answers the survey's
      // own scan, exactly as it always has.
      { query: { storey: opts.storey === undefined ? undefined : String(opts.storey) } },
    );
  }

  /**
   * `GET /api/surveys/:id/storeys` — the survey's floors, lowest first, and
   * which have a scan to draw.
   *
   * Needed before there is any geometry: the storeys a combined property
   * holds live in the database, not in any one capture, so a list built from
   * a single scan could never name the floor it was not drawn from.
   */
  async surveyStoreys(
    id: string,
  ): Promise<readonly { storey: number; label: string; hasScan: boolean }[]> {
    const res = await this.request<{
      storeys: { storey: number; label: string; hasScan: boolean }[];
    }>("GET", `/api/surveys/${encodeURIComponent(id)}/storeys`, {});
    return res.storeys;
  }

  /**
   * `GET /api/surveys/:id/scan-calibrations` — the survey's laser calibrations,
   * withdrawn ones included, each saying whether it `applies` to the current
   * capture.
   *
   * `null` on a 404: this page has already read the survey, so a 404 means the
   * API predates the route (migration 0040), and the scan then shows as
   * scanned rather than failing the page.
   */
  async scanCalibrations(surveyId: string): Promise<ScanCalibrationList | null> {
    try {
      return await this.request<ScanCalibrationList>(
        "GET",
        `/api/surveys/${encodeURIComponent(surveyId)}/scan-calibrations`,
        {},
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  }

  /**
   * `POST /api/surveys/:id/scan-calibrations`. The server re-evaluates the
   * readings against the stored capture; a refusal is a 422 whose message says
   * why (factor out of range, check wall disagrees).
   */
  recordScanCalibration(
    surveyId: string,
    input: NewScanCalibration,
  ): Promise<RecordedScanCalibration> {
    return this.request<RecordedScanCalibration>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/scan-calibrations`,
      { body: { ...input, source: "portal" } },
    );
  }

  /**
   * `DELETE /api/surveys/:id/scan-calibrations/:calibrationId` — withdraws it;
   * the record is kept. Answers what that did to the heat-loss rooms;
   * `undefined` from an older API that answered 204 with no body.
   */
  withdrawScanCalibration(
    surveyId: string,
    calibrationId: string,
  ): Promise<WithdrawnScanCalibration | undefined> {
    return this.request<WithdrawnScanCalibration | undefined>(
      "DELETE",
      `/api/surveys/${encodeURIComponent(surveyId)}/scan-calibrations/${encodeURIComponent(calibrationId)}`,
      {},
    );
  }

  /**
   * `GET /api/surveys/:id/space-model` — the status of the textured "3D Space"
   * model, and, once ready, the GLB to load.
   *
   * Answers `null` rather than throwing when the route is not there for this
   * survey: a 404 means no model has been requested, and a 501 means the API
   * has not shipped the pipeline yet (the endpoint is being added by another
   * workstream). Both are honest, non-error states the screen renders as "not
   * requested" — everything else, including a real failure to reach the API, is
   * left to propagate so the shared `Failure` view can explain it.
   */
  async spaceModel(id: string): Promise<SpaceModel | null> {
    try {
      const model = await this.request<SpaceModel | { readonly status: null }>(
        "GET",
        `/api/surveys/${encodeURIComponent(id)}/space-model`,
        {},
      );
      // The route answers 200 `{ status: null }` when the survey has no
      // Space capture at all — the same "not requested" as a 404.
      return model.status === null ? null : (model as SpaceModel);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 501)) {
        return null;
      }
      throw error;
    }
  }

  /**
   * `GET /api/surveys/:id/space-model/web` — the ready model's GLB bytes, for
   * `SpaceViewer` to parse. Fetched here, with the Bearer header, because
   * three's loaders fetch URLs themselves and cannot send one.
   */
  async spaceModelGlb(surveyId: string): Promise<ArrayBuffer> {
    const response = await this.sendRaw(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/space-model/web`,
      "model/gltf-binary",
    );
    if (!response.ok) throw await this.readError(response);
    return response.arrayBuffer();
  }

  /**
   * `GET /api/surveys/:id/space-model/content` — the USDZ, as an object URL
   * to save (iOS and macOS open it in Quick Look). The caller owns the URL
   * and must revoke it — see `photoImageObjectUrl`.
   */
  async spaceModelUsdz(surveyId: string): Promise<DownloadedFile> {
    const response = await this.sendRaw(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/space-model/content`,
      "model/vnd.usdz+zip",
    );
    if (!response.ok) throw await this.readError(response);
    const disposition = response.headers.get("content-disposition") ?? "";
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "space-model.usdz";
    return { url: URL.createObjectURL(await response.blob()), filename };
  }

  /**
   * `GET /api/surveys/:id/survey-model` — the whole survey as one
   * machine-readable JSON document (geometry, room schedule, heat loss and
   * design, assembled from the same loaders the PDF is drawn from). Returned as
   * an object URL to save; the caller owns the URL and must revoke it, as
   * `spaceModelUsdz` does. Needs the `heat-pump-design` module — a 403 surfaces
   * as an `ApiError` the caller can explain.
   */
  async surveyModelJson(surveyId: string): Promise<DownloadedFile> {
    const response = await this.sendRaw(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/survey-model`,
      "application/json",
    );
    if (!response.ok) throw await this.readError(response);
    const disposition = response.headers.get("content-disposition") ?? "";
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "survey-model.json";
    return { url: URL.createObjectURL(await response.blob()), filename };
  }

  // ------------------------------------------------ MCS design check

  /** `GET /api/surveys/:id/design-check` — see `DesignCheckResponse`. */
  designCheck(surveyId: string): Promise<DesignCheckResponse> {
    return this.request<DesignCheckResponse>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/design-check`,
      {},
    );
  }

  // -------------------------------------------------- floor plan objects

  /** `GET /api/surveys/:id/plan-objects` — every storey's objects, oldest first. */
  async planObjects(surveyId: string): Promise<PlanObject[]> {
    const res = await this.request<{ objects: PlanObject[] }>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-objects`,
      {},
    );
    return res.objects;
  }

  /**
   * `POST /api/surveys/:id/plan-objects`. The API fills a catalogue unit's
   * dimensions and a radiator's depth from the catalogue and refuses any that
   * disagree, so the object that comes back is the one to keep.
   */
  createPlanObject(surveyId: string, input: NewPlanObject): Promise<PlanObject> {
    return this.request<PlanObject>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-objects`,
      { body: input },
    );
  }

  /**
   * `PATCH /api/surveys/:id/plan-objects/:objectId`. With `baseVersion` —
   * the version the change was made on — the API refuses it (409
   * `version_conflict`) if someone else has changed or removed the object since.
   */
  updatePlanObject(
    surveyId: string,
    id: string,
    patch: PlanObjectPatch,
    baseVersion?: number,
  ): Promise<PlanObject> {
    return this.request<PlanObject>(
      "PATCH",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-objects/${encodeURIComponent(id)}`,
      { body: baseVersion === undefined ? patch : { ...patch, baseVersion } },
    );
  }

  /** `DELETE .../plan-objects/:objectId`; with `baseVersion`, refused if the object changed since. */
  deletePlanObject(surveyId: string, id: string, baseVersion?: number): Promise<void> {
    return this.request<void>(
      "DELETE",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-objects/${encodeURIComponent(id)}`,
      { query: { baseVersion } },
    );
  }

  // ------------------------------------- the hand-drawn plan (0047)

  /**
   * `GET /api/surveys/:id/drawing` — the floor plan a surveyor drew, for a
   * survey with no scan.
   *
   * `null` on a 404: the page has already read this survey, so a 404 here
   * means the API predates the route. The Survey page then simply does not
   * offer to draw, rather than failing.
   */
  async surveyDrawing(surveyId: string): Promise<SurveyDrawingState | null> {
    try {
      return await this.request<SurveyDrawingState>(
        "GET",
        `/api/surveys/${encodeURIComponent(surveyId)}/drawing`,
        {},
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  }

  /**
   * `PUT /api/surveys/:id/drawing` — the whole drawing, every save. It is one
   * small document and the editor holds all of it, so a save cannot land half
   * applied. `baseVersion` is the version the editor last read: the API
   * refuses (409 `version_conflict`) when somebody else has saved since.
   */
  saveSurveyDrawing(
    surveyId: string,
    plan: DrawnPlan,
    baseVersion?: number,
  ): Promise<SavedSurveyDrawing> {
    return this.request<SavedSurveyDrawing>(
      "PUT",
      `/api/surveys/${encodeURIComponent(surveyId)}/drawing`,
      { body: baseVersion === undefined ? { plan } : { plan, baseVersion } },
    );
  }

  /** `DELETE /api/surveys/:id/drawing` — throw the drawing away. */
  async deleteSurveyDrawing(surveyId: string): Promise<void> {
    await this.request<void>(
      "DELETE",
      `/api/surveys/${encodeURIComponent(surveyId)}/drawing`,
      {},
    );
  }

  /**
   * `GET /api/surveys/:id/ingest-export` — the survey as the agreed
   * `scannix-survey-ingest` hand-off document for another portal. `format:
   * "zip"` bundles it with the scan and photographs, each at the filename the
   * document references; otherwise it is the JSON alone. Returned as an object
   * URL to save, like `spaceModelUsdz`; the caller owns the URL.
   */
  async surveyIngestExport(
    surveyId: string,
    format: "json" | "zip" = "zip",
  ): Promise<DownloadedFile> {
    const response = await this.sendRaw(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/ingest-export${format === "zip" ? "?format=zip" : ""}`,
      format === "zip" ? "application/zip" : "application/json",
    );
    if (!response.ok) throw await this.readError(response);
    const disposition = response.headers.get("content-disposition") ?? "";
    const fallback = `scannix-survey.${format}`;
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallback;
    return { url: URL.createObjectURL(await response.blob()), filename };
  }

  /**
   * `GET /api/surveys/:id/photos-export` — the photographs, as a ZIP.
   *
   * The stamped copies, each with its position and capture time burned into
   * the image, so the pictures carry their own provenance once they leave
   * here. No manifest: this is the errand where somebody wants the pictures
   * and nothing else.
   */
  async surveyPhotosExport(surveyId: string): Promise<DownloadedFile> {
    const response = await this.sendRaw(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/photos-export`,
      "application/zip",
    );
    if (!response.ok) throw await this.readError(response);
    const disposition = response.headers.get("content-disposition") ?? "";
    const filename =
      /filename="([^"]+)"/.exec(disposition)?.[1] ?? "scannix-photos.zip";
    return { url: URL.createObjectURL(await response.blob()), filename };
  }

  /**
   * `DELETE /api/surveys/:id` — remove this survey and everything captured
   * under it (rooms, surfaces, photos, measurements, scans, share links).
   *
   * NO LONGER IRREVERSIBLE (0081). It goes to the recycle bin: out of the
   * portal at once, back again on request for thirty days, destroyed after
   * that. Owner/admin only (`survey: delete`), enforced again by the API.
   */
  async deleteSurvey(surveyId: string, reason?: string): Promise<void> {
    await this.request<void>(
      "DELETE",
      `/api/surveys/${encodeURIComponent(surveyId)}${reasonQuery(reason)}`,
      {},
    );
  }

  // ------------------------------------------------ the recycle bin

  /** `DELETE /api/photos/:id` — a photograph, into the bin. Owner only. */
  async deletePhoto(photoId: string, reason?: string): Promise<void> {
    await this.request<void>("DELETE", `/api/photos/${encodeURIComponent(photoId)}${reasonQuery(reason)}`, {});
  }

  /** `DELETE /api/documents/:id` — a document or signed agreement, into the bin. */
  async deleteDocument(documentId: string, reason?: string): Promise<void> {
    await this.request<void>("DELETE", `/api/documents/${encodeURIComponent(documentId)}${reasonQuery(reason)}`, {});
  }

  /** `DELETE /api/capture-sessions/:id` — a scan visit, into the bin. */
  async deleteCaptureSession(sessionId: string, reason?: string): Promise<void> {
    await this.request<void>("DELETE", `/api/capture-sessions/${encodeURIComponent(sessionId)}${reasonQuery(reason)}`, {});
  }

  /** `DELETE /api/surveys/:surveyId/supply-bills/:id` — a bill, into the bin. */
  async deleteSupplyBill(surveyId: string, billId: string, reason?: string): Promise<void> {
    await this.request<void>(
      "DELETE",
      `/api/surveys/${encodeURIComponent(surveyId)}/supply-bills/${encodeURIComponent(billId)}${reasonQuery(reason)}`,
      {},
    );
  }

  /** `GET /api/recycle-bin` — everything deleted and not yet purged. */
  async recycleBin(): Promise<{ items: RecycleItem[]; purgeAfterDays: number }> {
    return this.request<{ items: RecycleItem[]; purgeAfterDays: number }>("GET", "/api/recycle-bin", {});
  }

  /** `POST /api/recycle-bin/:kind/:id/restore` — put it back. */
  async restoreFromRecycleBin(kind: string, id: string): Promise<void> {
    await this.request<void>(
      "POST",
      `/api/recycle-bin/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/restore`,
      {},
    );
  }

  /** `POST /api/recycle-bin/purge` — destroy everything past its thirty days. */
  async purgeRecycleBin(): Promise<{ destroyed: number }> {
    return this.request<{ destroyed: number }>("POST", "/api/recycle-bin/purge", {});
  }

  // ------------------------------------------------ design conditions

  /** `GET /api/surveys/:id/design-conditions` — the conditions the heat loss is calculated at (0056). */
  designConditions(surveyId: string): Promise<DesignConditionsView> {
    return this.request<DesignConditionsView>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/design-conditions`,
      {},
    );
  }

  /** Changes some of them: a Table 2 location and column, or a typed temperature; the heat loss follows. */
  setDesignConditions(
    surveyId: string,
    input: {
      readonly location?: string;
      readonly column?: "A" | "B";
      readonly externalDesignTempC?: number;
      readonly annualAverageExternalTempC?: number;
      readonly thermalBridgingFactor?: number | null;
      readonly countHeatBetweenRooms?: boolean;
    },
  ): Promise<DesignConditionsView> {
    return this.request<DesignConditionsView>(
      "PUT",
      `/api/surveys/${encodeURIComponent(surveyId)}/design-conditions`,
      { body: input },
    );
  }

  // ------------------------------------------------ default materials

  /** `GET /api/surveys/:id/default-materials` — one row per kind of surface (0056). */
  async defaultMaterials(surveyId: string): Promise<readonly DefaultMaterialRow[]> {
    const res = await this.request<{ rows: readonly DefaultMaterialRow[] }>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/default-materials`,
      {},
    );
    return res.rows;
  }

  /** Sets a kind of surface's default construction; every surface not set by hand follows. */
  setDefaultMaterial(
    surveyId: string,
    target: string,
    constructionId: string,
  ): Promise<{ readonly applied: number; readonly kept: number; readonly rows: readonly DefaultMaterialRow[] }> {
    return this.request(
      "PUT",
      `/api/surveys/${encodeURIComponent(surveyId)}/default-materials/${encodeURIComponent(target)}`,
      { body: { constructionId } },
    );
  }

  // ------------------------------------------------ floor-plan pictures

  /** `GET /api/surveys/:id/plan-images` — the picture under each storey, if any (0057). */
  async planImages(surveyId: string): Promise<readonly PlanImageView[]> {
    const res = await this.request<{ images: readonly PlanImageView[] }>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-images`,
      {},
    );
    return res.images;
  }

  /** Puts a PNG or JPEG under a storey (replacing any there), unscaled. */
  async uploadPlanImage(surveyId: string, storey: number, file: Blob): Promise<PlanImageView> {
    const response = await this.sendRaw(
      "PUT",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-images/${storey}`,
      "application/json",
      { raw: { data: file, contentType: file.type || "application/octet-stream" } },
    );
    if (!response.ok) throw await this.readError(response);
    return (await response.json()) as PlanImageView;
  }

  /** `GET /api/surveys/:surveyId/supply-bills` — the bills sent from the phone. */
  async supplyBills(surveyId: string): Promise<SupplyBill[]> {
    const res = await this.request<{ bills: SupplyBill[] }>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/supply-bills`,
      {},
    );
    return res.bills;
  }

  /** A bill's file as an object URL; the caller revokes it. */
  async supplyBillObjectUrl(surveyId: string, id: string): Promise<string> {
    const response = await this.sendRaw(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/supply-bills/${encodeURIComponent(id)}/file`,
      "image/*, application/pdf",
    );
    if (!response.ok) throw await this.readError(response);
    return URL.createObjectURL(await response.blob());
  }

  /** The picture's bytes as an object URL; the caller revokes it. */
  async planImageObjectUrl(surveyId: string, storey: number): Promise<string> {
    const response = await this.sendRaw(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-images/${storey}/image`,
      "image/*",
    );
    if (!response.ok) throw await this.readError(response);
    return URL.createObjectURL(await response.blob());
  }

  updatePlanImage(
    surveyId: string,
    storey: number,
    patch: { readonly metresPerPx?: number; readonly originX?: number; readonly originY?: number; readonly opacity?: number; readonly hidden?: boolean },
  ): Promise<PlanImageView> {
    return this.request<PlanImageView>(
      "PATCH",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-images/${storey}`,
      { body: patch },
    );
  }

  async deletePlanImage(surveyId: string, storey: number): Promise<void> {
    const response = await this.sendRaw(
      "DELETE",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-images/${storey}`,
      "application/json",
    );
    if (!response.ok) throw await this.readError(response);
  }

  // ------------------------------------------------ room notes

  /** `GET /api/surveys/:id/room-notes` — every room's photos and notes, by scan id (0056). */
  async roomNotes(surveyId: string): Promise<Readonly<Record<string, RoomNotesView>>> {
    const res = await this.request<{ rooms: Readonly<Record<string, RoomNotesView>> }>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/room-notes`,
      {},
    );
    return res.rooms;
  }

  setRoomNotes(
    surveyId: string,
    roomRef: string,
    input: {
      readonly notes?: string;
      readonly photoIds?: readonly string[];
      readonly extractDevices?: readonly import("@sts/schemas").ExtractDevice[];
      readonly radiatorSpaces?: readonly import("@sts/schemas").RadiatorSpace[];
    },
  ): Promise<RoomNotesView> {
    return this.request<RoomNotesView>(
      "PUT",
      `/api/surveys/${encodeURIComponent(surveyId)}/room-notes/${encodeURIComponent(roomRef)}`,
      { body: input },
    );
  }

  // ------------------------------------------------ site survey

  /** `GET /api/surveys/:id/site-survey` — every site-survey section's answers (0056). */
  async siteSurvey(surveyId: string): Promise<SiteSurveyAnswersBySection> {
    const res = await this.request<{ sections: SiteSurveyAnswersBySection }>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/site-survey`,
      {},
    );
    return res.sections;
  }

  /** Answers some of a section's questions; `null` clears one. Answers the section as it now stands. */
  patchSiteSurvey(
    surveyId: string,
    section: string,
    answers: Readonly<Record<string, unknown>>,
  ): Promise<SiteSurveySectionAnswers> {
    return this.request<SiteSurveySectionAnswers>(
      "PATCH",
      `/api/surveys/${encodeURIComponent(surveyId)}/site-survey/${encodeURIComponent(section)}`,
      { body: { answers } },
    );
  }

  // ------------------------------------------------ plan orientation

  /** `GET /api/surveys/:id/plan-orientation` — which side of the plan is the front (0055). */
  planOrientation(surveyId: string): Promise<SurveyPlanOrientation> {
    return this.request<SurveyPlanOrientation>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-orientation`,
      {},
    );
  }

  /** Saves the plan's quarter turns (0–3), or null to let the entrance door decide. */
  setPlanOrientation(surveyId: string, quarterTurns: number | null): Promise<SurveyPlanOrientation> {
    return this.request<SurveyPlanOrientation>(
      "PUT",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-orientation`,
      { body: { quarterTurns } },
    );
  }

  // ------------------------------------------------ ready for design

  /** `GET /api/surveys/:id/design-readiness` — what a design pack still lacks. */
  designReadiness(surveyId: string): Promise<DesignReadiness> {
    return this.request<DesignReadiness>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/design-readiness`,
      {},
    );
  }

  /** `GET /api/constructions?element=…` — the RdSAP construction library. */
  async constructions(element: ConstructionElement): Promise<readonly Construction[]> {
    const res = await this.request<{ constructions: readonly Construction[] }>(
      "GET",
      `/api/constructions?element=${encodeURIComponent(element)}`,
      {},
    );
    return res.constructions;
  }

  /** A roof on each room, from a roof construction. */
  addRoofs(surveyId: string, roomIds: readonly string[], constructionId: string): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/fabric/roofs`,
      { body: { roomIds, constructionId } },
    );
  }

  /** A ground floor on each room that has none. */
  addGroundFloors(surveyId: string, roomIds: readonly string[]): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/fabric/ground-floors`,
      { body: { roomIds } },
    );
  }

  /** One construction for every external wall nobody has confirmed. */
  applyExternalWallConstruction(surveyId: string, constructionId: string): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/fabric/external-walls`,
      { body: { constructionId } },
    );
  }

  /** Take the roofs the card added off these rooms. */
  removeRoofs(surveyId: string, roomIds: readonly string[]): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/fabric/roofs/remove`,
      { body: { roomIds } },
    );
  }

  /** What is under every heated room of a storey. */
  setStoreyFloor(surveyId: string, level: number, answer: StoreyFloorAnswer): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/storeys/${level}/floor`,
      { body: answer },
    );
  }

  /** What is over every heated room of a storey: a heated floor, or a roof. */
  setStoreyCeiling(surveyId: string, level: number, answer: StoreyCeilingAnswer): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/storeys/${level}/ceiling`,
      { body: answer },
    );
  }

  /** Which rooms are the extension (0058); an empty list: one building. */
  setExtensionRooms(surveyId: string, roomIds: readonly string[]): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "PUT",
      `/api/surveys/${encodeURIComponent(surveyId)}/extension`,
      { body: { roomIds } },
    );
  }

  /** The floor of the whole property, the main building or the extension — its lowest rooms. */
  setPartFloor(surveyId: string, part: "whole" | "main" | "extension", answer: StoreyFloorAnswer): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/parts/${part}/floor`,
      { body: answer },
    );
  }

  /** The roof of the whole property, the main building or the extension — over its highest rooms. */
  setPartCeiling(surveyId: string, part: "whole" | "main" | "extension", answer: StoreyCeilingAnswer): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/parts/${part}/ceiling`,
      { body: answer },
    );
  }

  /** What is under one room — a flat over a shop, a room over a garage. */
  setRoomFloor(surveyId: string, roomId: string, answer: StoreyFloorAnswer): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/rooms/${encodeURIComponent(roomId)}/floor`,
      { body: answer },
    );
  }

  /** What is over one room: a heated room, a roof, or another home. */
  setRoomCeiling(surveyId: string, roomId: string, answer: StoreyCeilingAnswer): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/rooms/${encodeURIComponent(roomId)}/ceiling`,
      { body: answer },
    );
  }

  /** Accept each room's floor area as it stands. */
  confirmFloorAreas(surveyId: string, roomIds: readonly string[]): Promise<DesignReadinessWrite> {
    return this.request<DesignReadinessWrite>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/confirm-floor-areas`,
      { body: { roomIds } },
    );
  }

  // ------------------------------------- the plan's edit layer (0039)

  /**
   * `GET /api/surveys/:id/plan-edits` — every correction to the scan's plan.
   *
   * `null` on a 404: the page has already read this survey, so a 404 here
   * means the API predates the route, and the plan then shows the scan as
   * captured and says it cannot be corrected here — rather than failing.
   */
  async planEdits(surveyId: string): Promise<PlanEditList | null> {
    try {
      return await this.request<PlanEditList>(
        "GET",
        `/api/surveys/${encodeURIComponent(surveyId)}/plan-edits`,
        {},
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  }

  /**
   * `POST /api/surveys/:id/plan-edits/batch` — all or nothing; answers the
   * whole list. `baseVersions`, by `planEditKey`, is the version of each
   * element's correction the change was made on (0: none): the API refuses
   * the whole batch (409 `version_conflict`) if any has moved on since.
   */
  planEditBatch(
    surveyId: string,
    batch: PlanEditBatch,
    baseVersions?: ReadonlyMap<string, number>,
  ): Promise<PlanEditList> {
    const withBase = <T extends { targetKind: string; targetId: string }>(item: T): T => {
      const base = baseVersions?.get(`${item.targetKind}:${item.targetId}`);
      return base === undefined ? item : { ...item, baseVersion: base };
    };
    return this.request<PlanEditList>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-edits/batch`,
      { body: { upsert: batch.upsert.map(withBase), revert: batch.revert.map(withBase) } },
    );
  }

  /** `GET /api/surveys/:id/plan-dimensions`; `null` on a 404 (an API older than the route). */
  async planDimensions(surveyId: string): Promise<PlanDimensionRecord[] | null> {
    try {
      const res = await this.request<{ dimensions: PlanDimensionRecord[] }>(
        "GET",
        `/api/surveys/${encodeURIComponent(surveyId)}/plan-dimensions`,
        {},
      );
      return res.dimensions;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  }

  createPlanDimension(surveyId: string, input: NewPlanDimension): Promise<PlanDimensionRecord> {
    return this.request<PlanDimensionRecord>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-dimensions`,
      { body: input },
    );
  }

  updatePlanDimension(surveyId: string, id: string, patch: PlanDimensionPatch): Promise<PlanDimensionRecord> {
    return this.request<PlanDimensionRecord>(
      "PATCH",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-dimensions/${encodeURIComponent(id)}`,
      { body: patch },
    );
  }

  deletePlanDimension(surveyId: string, id: string): Promise<void> {
    return this.request<void>(
      "DELETE",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-dimensions/${encodeURIComponent(id)}`,
      {},
    );
  }

  // -------------------------------- comments pinned to the plan (0043)

  /**
   * `GET /api/surveys/:id/plan-comments` — every thread, by number.
   * `null` on a 404: the page has already read this survey, so a 404 here
   * means an API older than the route, and the plan says comments are not
   * kept rather than failing.
   */
  async planComments(surveyId: string): Promise<PlanCommentThread[] | null> {
    try {
      const res = await this.request<{ threads: PlanCommentThread[] }>(
        "GET",
        `/api/surveys/${encodeURIComponent(surveyId)}/plan-comments`,
        {},
      );
      return res.threads;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  }

  createPlanComment(surveyId: string, input: NewPlanComment): Promise<PlanCommentThread> {
    return this.request<PlanCommentThread>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-comments`,
      { body: input },
    );
  }

  replyToPlanComment(surveyId: string, threadId: string, body: string): Promise<PlanCommentThread> {
    return this.request<PlanCommentThread>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-comments/${encodeURIComponent(threadId)}/replies`,
      { body: { body } },
    );
  }

  /** New text for a comment or reply (its author only), or a thread resolved / reopened. */
  updatePlanComment(surveyId: string, id: string, patch: PlanCommentPatch): Promise<PlanCommentThread> {
    return this.request<PlanCommentThread>(
      "PATCH",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-comments/${encodeURIComponent(id)}`,
      { body: patch },
    );
  }

  /** A thread with its replies, or one reply. */
  deletePlanComment(surveyId: string, id: string): Promise<void> {
    return this.request<void>(
      "DELETE",
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-comments/${encodeURIComponent(id)}`,
      {},
    );
  }

  // ------------------------------------------- stock and the installer pack

  /** `GET /api/surveys/:id/stock` — the report, recomputed on every read. */
  surveyStock(surveyId: string): Promise<SurveyStock> {
    return this.request<SurveyStock>("GET", `/api/surveys/${encodeURIComponent(surveyId)}/stock`, {});
  }

  /**
   * `PATCH /api/surveys/:id/stock/adjustments`. Settings merge field by field
   * and lines key by key (null removes one); manual lines replace whole. The
   * answer is the recomputed stock, so the screen never shows a figure the
   * server would not produce.
   */
  patchStockAdjustments(surveyId: string, patch: StockAdjustmentsPatch): Promise<SurveyStock> {
    return this.request<SurveyStock>(
      "PATCH",
      `/api/surveys/${encodeURIComponent(surveyId)}/stock/adjustments`,
      { body: patch },
    );
  }

  async stockCatalogue(): Promise<StockCatalogueItem[]> {
    const res = await this.request<{ items: StockCatalogueItem[] }>("GET", "/api/stock/catalogue", {});
    return res.items;
  }

  createStockCatalogueItem(input: NewStockCatalogueItem): Promise<StockCatalogueItem> {
    return this.request<StockCatalogueItem>("POST", "/api/stock/catalogue", { body: input });
  }

  updateStockCatalogueItem(id: string, patch: StockCatalogueItemPatch): Promise<StockCatalogueItem> {
    return this.request<StockCatalogueItem>("PATCH", `/api/stock/catalogue/${encodeURIComponent(id)}`, {
      body: patch,
    });
  }

  deleteStockCatalogueItem(id: string): Promise<void> {
    return this.request<void>("DELETE", `/api/stock/catalogue/${encodeURIComponent(id)}`, {});
  }

  /** `GET /api/surveys/:id/installer-pack` — the draft pack's HTML, for a sandboxed preview. */
  async installerPackHtml(surveyId: string): Promise<string> {
    const response = await this.sendRaw(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/installer-pack`,
      "text/html",
    );
    if (!response.ok) throw await this.readError(response);
    return response.text();
  }

  /**
   * The draft pack as a PDF, fetched with the Bearer header a plain `<a href>`
   * could not send, and handed back as an object URL. The caller owns the URL
   * and must revoke it — see `photoImageObjectUrl`.
   */
  installerPackPdf(surveyId: string): Promise<DownloadedFile> {
    return this.downloadPdf(`/api/surveys/${encodeURIComponent(surveyId)}/installer-pack/pdf`);
  }

  /** An issued pack's PDF, printed from its stored, hash-checked bytes. */
  issuedInstallerPackPdf(surveyId: string, documentId: string): Promise<DownloadedFile> {
    return this.downloadPdf(
      `/api/surveys/${encodeURIComponent(surveyId)}/installer-pack/documents/${encodeURIComponent(documentId)}/pdf`,
    );
  }

  /** `POST /api/surveys/:id/installer-pack/issue` — stores the pack as an immutable document. */
  async issueInstallerPack(surveyId: string): Promise<DocumentSummary> {
    const res = await this.request<{ document: DocumentSummary }>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/installer-pack/issue`,
      {},
    );
    return res.document;
  }

  private downloadPdf(path: string): Promise<DownloadedFile> {
    return this.downloadFile(path, "application/pdf", "installer-pack.pdf");
  }

  // ------------------------------------------ plan exports and room schedule

  /**
   * `GET /api/surveys/:id/plan-export/:format` — the floor plan as a file:
   * PDF (every storey unless one is named), PNG, SVG or layered DXF (one
   * storey; the lowest when none is named). Fetched with the Bearer header and
   * handed back as an object URL the caller must revoke, like the installer
   * pack's PDF.
   */
  planExport(
    surveyId: string,
    format: PlanExportFormat,
    opts: {
      readonly storey?: number;
      readonly paper?: PlanPaperSize;
      /** Draw the open comments' numbered pins (0043). Off unless asked: exports leave the building. */
      readonly commentPins?: boolean;
    } = {},
  ): Promise<DownloadedFile> {
    const query = new URLSearchParams();
    if (opts.storey !== undefined) query.set("storey", String(opts.storey));
    if (opts.paper !== undefined) query.set("paper", opts.paper);
    if (opts.commentPins === true) query.set("pins", "open");
    const qs = query.toString();
    return this.downloadFile(
      `/api/surveys/${encodeURIComponent(surveyId)}/plan-export/${format}${qs ? `?${qs}` : ""}`,
      PLAN_EXPORT_ACCEPT[format],
      `plan.${format}`,
    );
  }

  /** `GET /api/surveys/:id/room-schedule/:format` — the room schedule as PDF or CSV. */
  roomSchedule(surveyId: string, format: "pdf" | "csv"): Promise<DownloadedFile> {
    return this.downloadFile(
      `/api/surveys/${encodeURIComponent(surveyId)}/room-schedule/${format}`,
      format === "pdf" ? "application/pdf" : "text/csv",
      `room-schedule.${format}`,
    );
  }

  /** Any attachment route: bytes → blob → object URL, named as the server named it. */
  private async downloadFile(path: string, accept: string, fallbackName: string): Promise<DownloadedFile> {
    const response = await this.sendRaw("GET", path, accept);
    if (!response.ok) throw await this.readError(response);
    const disposition = response.headers.get("content-disposition") ?? "";
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
    return { url: URL.createObjectURL(await response.blob()), filename };
  }

  async assumptions(id: string): Promise<Assumption[]> {
    const res = await this.request<{ assumptions: Assumption[] }>(
      "GET",
      `/api/surveys/${encodeURIComponent(id)}/assumptions`,
      {},
    );
    return res.assumptions;
  }

  /**
   * `compliance: true` asks the API to refuse rather than warn. A refusal
   * arrives as a 422 `compliance_failure` carrying every rule breached, which
   * is the useful half of "we cannot issue this".
   */
  heatLoss(
    id: string,
    opts: { compliance?: boolean } = {},
  ): Promise<SurveyHeatLoss> {
    return this.request<SurveyHeatLoss>(
      "GET",
      `/api/surveys/${encodeURIComponent(id)}/heat-loss`,
      { query: { compliance: opts.compliance === true ? "true" : undefined } },
    );
  }

  confirmUValue(
    surfaceId: string,
    input: ConfirmUValueInput = {},
  ): Promise<ConfirmUValueResult> {
    return this.request<ConfirmUValueResult>(
      "POST",
      `/api/surfaces/${encodeURIComponent(surfaceId)}/confirm-u-value`,
      { body: input },
    );
  }

  /**
   * `POST /api/surfaces/:id/adjacency` — what is on the other side of a wall:
   * a party wall, an unheated space (each with the designer's temperature),
   * or a confirmed outside wall. The wall's windows and doors follow it, and
   * the answer is recorded as confirmed by whoever gave it. Same grant as
   * `confirmUValue` (`survey: update`).
   */
  setWallAdjacency(surfaceId: string, input: WallAdjacencyInput): Promise<WallAdjacencyResult> {
    return this.request<WallAdjacencyResult>(
      "POST",
      `/api/surfaces/${encodeURIComponent(surfaceId)}/adjacency`,
      { body: input },
    );
  }

  /**
   * `PATCH /api/rooms/:id` — the room panel: name, room type, internal design
   * temperature, air change rate and "mark as unheated", recorded with who
   * and when. A field left out is left alone; `designTempC: null` goes back to
   * the MIS 3005-D Table 1 minimum and `airChangesPerHour: null` back to the
   * figure the scan conversion calculated. Same grant as `confirmUValue`
   * (`survey: update`), and refused once the survey is complete.
   */
  updateRoomDetails(roomId: string, input: RoomDetailsInput): Promise<RoomDetailsResult> {
    return this.request<RoomDetailsResult>(
      "PATCH",
      `/api/rooms/${encodeURIComponent(roomId)}`,
      { body: input },
    );
  }

  /**
   * `PATCH /api/surveys/:id/property` — records any of the survey's four
   * property facts. Omitted fields are left alone; `null` clears one.
   *
   * Gated on `survey: update` like `completeSurvey`. The API answers 409
   * `survey_locked` once the survey is complete, 400 naming the field for a
   * value outside the vocabulary.
   */
  async updateSurveyProperty(
    id: string,
    input: SurveyPropertyInput,
  ): Promise<SurveyPropertyDetails> {
    const res = await this.request<{ property: SurveyPropertyDetails }>(
      "PATCH",
      `/api/surveys/${encodeURIComponent(id)}/property`,
      { body: input },
    );
    return res.property;
  }

  /**
   * Marks the survey finished. After this the API holds it read-only.
   *
   * Gated on `survey: update`, the same grant `confirmUValue` needs — a
   * surveyor holds it at "own" scope, a designer or technical supervisor at
   * "org". The API itself refuses a survey with no rooms recorded yet, which
   * is the one precondition worth stating rather than discovering by pressing
   * the button.
   */
  completeSurvey(id: string): Promise<{ id: string; status: string }> {
    return this.request<{ id: string; status: string }>(
      "POST",
      `/api/surveys/${encodeURIComponent(id)}/complete`,
      {},
    );
  }

  // ------------------------------------------------- PAS 2035 retrofit
  //
  // Read routes for the project itself (list, detail, measures, readiness),
  // plus — below — the eight TrustMark RIAPI write methods that landed with
  // `retrofit-trustmark-routes.ts`. Every retrofit write is still gated on a
  // PAS role held per project rather than per organisation; the service layer
  // is where those live, unchanged.

  /** `GET /api/retrofit/projects` — `{ projects: [...] }`. */
  async retrofitProjects(): Promise<RetrofitProjectListItem[]> {
    const res = await this.request<{ projects: RetrofitProjectListItem[] }>(
      "GET",
      "/api/retrofit/projects",
      {},
    );
    return res.projects;
  }

  /** `GET /api/retrofit/projects/:id`. */
  retrofitProject(id: string): Promise<RetrofitProjectDetail> {
    return this.request<RetrofitProjectDetail>(
      "GET",
      `/api/retrofit/projects/${encodeURIComponent(id)}`,
      {},
    );
  }

  /** `GET /api/retrofit/projects/:id/measures`. */
  retrofitMeasures(id: string): Promise<RetrofitMeasurePackage> {
    return this.request<RetrofitMeasurePackage>(
      "GET",
      `/api/retrofit/projects/${encodeURIComponent(id)}/measures`,
      {},
    );
  }

  /** `GET /api/retrofit/projects/:id/readiness`. */
  retrofitReadiness(id: string): Promise<RetrofitReadiness> {
    return this.request<RetrofitReadiness>(
      "GET",
      `/api/retrofit/projects/${encodeURIComponent(id)}/readiness`,
      {},
    );
  }

  // ---------------------------- PAS 2035 retrofit — TrustMark lodgement
  //
  // The eight RIAPI operations, one method each. Every write is gated
  // server-side on `design:update` (designer / technical-supervisor / owner /
  // admin) AND on the caller holding the live PAS role the service asks for.
  // A deployment with no TrustMark credentials answers 503 with
  // `ApiError.code === "not_configured"`; the panel that drives these
  // methods should render a "TrustMark not connected" banner in that case
  // rather than treating a configuration state as a broken action.

  /** `POST /api/retrofit/projects/:id/trustmark/submit-assessment` — AssessmentSubmit. */
  submitAssessmentToTrustmark(
    projectId: string,
    body: TrustmarkAssessmentSubmitBody,
  ): Promise<unknown> {
    return this.request<unknown>(
      "POST",
      `/api/retrofit/projects/${encodeURIComponent(projectId)}/trustmark/submit-assessment`,
      { body },
    );
  }

  /** `POST /api/retrofit/projects/:id/trustmark/start-project` — ProjectStart. */
  startTrustmarkProject(
    projectId: string,
    body: TrustmarkStartProjectBody,
  ): Promise<unknown> {
    return this.request<unknown>(
      "POST",
      `/api/retrofit/projects/${encodeURIComponent(projectId)}/trustmark/start-project`,
      { body },
    );
  }

  /** `POST /api/retrofit/projects/:id/trustmark/measures/:measureId/lodge` — LodgementSubmit. */
  lodgeTrustmarkMeasure(
    projectId: string,
    measureId: string,
    body: TrustmarkMeasureLodgeBody,
  ): Promise<unknown> {
    return this.request<unknown>(
      "POST",
      `/api/retrofit/projects/${encodeURIComponent(projectId)}/trustmark/measures/${encodeURIComponent(measureId)}/lodge`,
      { body },
    );
  }

  /** `POST /api/retrofit/projects/:id/trustmark/complete-project` — ProjectComplete. */
  completeTrustmarkProject(
    projectId: string,
    body: TrustmarkCompleteProjectBody,
  ): Promise<unknown> {
    return this.request<unknown>(
      "POST",
      `/api/retrofit/projects/${encodeURIComponent(projectId)}/trustmark/complete-project`,
      { body },
    );
  }

  /** `POST /api/retrofit/projects/:id/trustmark/void-project` — ProjectVoid. */
  voidTrustmarkProject(projectId: string): Promise<unknown> {
    return this.request<unknown>(
      "POST",
      `/api/retrofit/projects/${encodeURIComponent(projectId)}/trustmark/void-project`,
      { body: {} },
    );
  }

  /** `POST /api/retrofit/projects/:id/trustmark/amend-lodgement` — LodgementAmend. */
  amendTrustmarkLodgement(
    projectId: string,
    body: TrustmarkAmendLodgementBody,
  ): Promise<unknown> {
    return this.request<unknown>(
      "POST",
      `/api/retrofit/projects/${encodeURIComponent(projectId)}/trustmark/amend-lodgement`,
      { body },
    );
  }

  /** `POST /api/retrofit/projects/:id/trustmark/update-property-information`. */
  updateTrustmarkPropertyInformation(
    projectId: string,
    body: TrustmarkPropertyInformationUpdate,
  ): Promise<unknown> {
    return this.request<unknown>(
      "POST",
      `/api/retrofit/projects/${encodeURIComponent(projectId)}/trustmark/update-property-information`,
      { body },
    );
  }

  /** `POST /api/retrofit/projects/:id/trustmark/amend-project` — ProjectAmend. */
  amendTrustmarkProject(
    projectId: string,
    body: TrustmarkCompleteProjectBody,
  ): Promise<unknown> {
    return this.request<unknown>(
      "POST",
      `/api/retrofit/projects/${encodeURIComponent(projectId)}/trustmark/amend-project`,
      { body },
    );
  }

  // ------------------------------------------------------------ design decisions
  //
  // The job's design decisions (heat pump make/model/capacity, cylinder,
  // metering, occupancy) plus per-room emitter decisions, plus — the useful
  // part — the same figures GET /api/jobs/:jobId/reports/design-pack would
  // compute from them, including checkSizing / assessEmitters warnings.
  // The HeatPumpSelection and EmitterSelection screens both drive this
  // endpoint: PUT the typed product, GET the verdict inline.

  designDecisions(jobId: string): Promise<DesignDecisionsResponse> {
    return this.request<DesignDecisionsResponse>(
      "GET",
      `/api/jobs/${encodeURIComponent(jobId)}/design-decisions`,
      {},
    );
  }

  updateDesignDecisions(
    jobId: string,
    patch: DesignDecisionsPatch,
  ): Promise<unknown> {
    return this.request<unknown>(
      "PUT",
      `/api/jobs/${encodeURIComponent(jobId)}/design-decisions`,
      { body: patch },
    );
  }

  updateRoomEmitter(
    jobId: string,
    roomId: string,
    input: RoomEmitterInput,
  ): Promise<RoomEmitterDecision> {
    return this.request<RoomEmitterDecision>(
      "PUT",
      `/api/jobs/${encodeURIComponent(jobId)}/design-decisions/emitters/${encodeURIComponent(roomId)}`,
      { body: input },
    );
  }

  deleteRoomEmitter(jobId: string, roomId: string): Promise<void> {
    return this.request<void>(
      "DELETE",
      `/api/jobs/${encodeURIComponent(jobId)}/design-decisions/emitters/${encodeURIComponent(roomId)}`,
      {},
    );
  }

  // -------------------------------------------------------- Boiler Upgrade Scheme
  //
  // The API's own header (bus-routes.ts) explains why the eligibility check is
  // a POST rather than a GET: `system` has no persisted home in this schema
  // and must be sent in the body every time. This client mirrors that
  // faithfully — no attempt to fake a cache, and every warning and reason
  // reaches the caller untouched (never truncated, filtered, or summarised).

  /**
   * `POST /api/jobs/:jobId/bus-eligibility` — the grant verdict for one job.
   *
   * Sits behind the heat-pump-design entitlement server-side; a 403 from this
   * call means the organisation is not entitled to the module (`ApiError.code`
   * is `entitlement_required`).
   */
  busEligibility(
    jobId: string,
    request: BusEligibilityRequest,
  ): Promise<BusEligibilityResponse> {
    return this.request<BusEligibilityResponse>(
      "POST",
      `/api/jobs/${encodeURIComponent(jobId)}/bus-eligibility`,
      { body: request },
    );
  }

  /**
   * `GET /api/jobs/:jobId/bus-deadlines` — the two BUS clocks (application
   * and voucher expiry). `voucherExpiry` is only computed when the caller
   * supplies both `voucherIssuedOn` and `technology`; the server does not
   * persist either.
   */
  busDeadlines(
    jobId: string,
    query: BusDeadlinesQuery = {},
  ): Promise<BusDeadlinesResponse> {
    return this.request<BusDeadlinesResponse>(
      "GET",
      `/api/jobs/${encodeURIComponent(jobId)}/bus-deadlines`,
      {
        query: {
          on: query.on,
          voucherIssuedOn: query.voucherIssuedOn,
          technology: query.technology,
        },
      },
    );
  }

  // ------------------------------------------------------------- documents

  async searchDocuments(
    query: DocumentSearchQuery,
  ): Promise<DocumentSearchResult[]> {
    const res = await this.request<{ results: DocumentSearchResult[] }>(
      "GET",
      "/api/documents/search",
      {
        query: {
          q: query.q,
          kind: query.kind,
          stage: query.stage,
          technology: query.technology,
          onlyAvailable: query.onlyAvailable === true ? "true" : undefined,
          limit: query.limit,
        },
      },
    );
    return res.results;
  }

  /** `GET /api/jobs/:jobId/documents` — every document on a job, newest first. */
  async jobDocuments(jobId: string): Promise<ListedDocument[]> {
    const res = await this.request<{ documents: ListedDocument[] }>(
      "GET",
      `/api/jobs/${encodeURIComponent(jobId)}/documents`,
      {},
    );
    return res.documents;
  }

  getDocument(id: string): Promise<DocumentSummary> {
    return this.request<DocumentSummary>(
      "GET",
      `/api/documents/${encodeURIComponent(id)}`,
      {},
    );
  }

  /**
   * Asks whether the stored bytes still hash to what was recorded.
   *
   * A failed check comes back 200 with `ok: false` and a reason, because the
   * caller asked a question and got a true answer. Only `/content` refuses.
   */
  verifyDocument(id: string): Promise<DocumentIntegrity> {
    return this.request<DocumentIntegrity>(
      "GET",
      `/api/documents/${encodeURIComponent(id)}/verify`,
      {},
    );
  }

  /**
   * The document's own bytes, re-verified by the API before they leave.
   *
   * Comes back as HTML, not JSON — `/verify` answers a question about the
   * bytes, this route hands them over — and a request that fails the
   * integrity check is refused outright rather than served with a warning, so
   * reaching this method's return at all is itself part of what it proves.
   * The hash travels alongside in `X-Document-Hash`, read from the header
   * because that is where the API places what it just checked the bytes
   * against.
   */
  async documentContent(id: string): Promise<DocumentContent> {
    const response = await this.sendRaw(
      "GET",
      `/api/documents/${encodeURIComponent(id)}/content`,
      "text/html",
    );
    if (!response.ok) {
      const error = await this.readError(response);
      if (error.status === 401) this.onUnauthorised?.(error);
      throw error;
    }
    return {
      html: await response.text(),
      contentHash: response.headers.get("x-document-hash") ?? "",
    };
  }

  /**
   * Fixes a document in place: hashed, immutable, retained as evidence.
   *
   * A draft cannot be sent or signed — both routes refuse it — so this is the
   * step that makes either possible. Gated on `document: issue`, a grant held
   * separately from `create`: producing a draft and publishing it as evidence
   * are different acts carrying different risk.
   */
  issueDocument(id: string): Promise<DocumentSummary> {
    return this.request<DocumentSummary>(
      "POST",
      `/api/documents/${encodeURIComponent(id)}/issue`,
      {},
    );
  }

  /**
   * Emails a document. Outward-facing and irreversible: nothing in this client
   * should call it that a person has not just confirmed.
   */
  sendDocument(
    id: string,
    input: SendDocumentInput,
  ): Promise<SendDocumentResult> {
    return this.request<SendDocumentResult>(
      "POST",
      `/api/documents/${encodeURIComponent(id)}/send`,
      { body: input },
    );
  }

  /**
   * `POST /api/documents/:id/signing-link` — a link to hand over by hand.
   *
   * For the installer standing in a kitchen who wants to put the link into a
   * text message or read it down the phone. The URL comes back EXACTLY ONCE:
   * the server stores only its hash, so if this response is lost the link is
   * gone and a new one has to be minted. Nothing here may cache it, and the
   * screen must not keep it after the user has finished with it.
   */
  signingLink(id: string): Promise<SigningLinkResult> {
    return this.request<SigningLinkResult>(
      "POST",
      `/api/documents/${encodeURIComponent(id)}/signing-link`,
      {},
    );
  }

  /**
   * `POST /api/jobs/:jobId/contract/issue` — the whole thing, in one press.
   *
   * Produces the agreement, fixes it as evidence, raises the signature request
   * and mints the link, then hands back the link to open. The steps still
   * happen in that order through the same services; they are simply no longer
   * five things for a person to remember and sequence.
   *
   * Refuses, naming every term it is missing, when the agreement cannot be
   * produced — one button cannot invent where a contract was agreed.
   */
  async issueContract(
    jobId: string,
  ): Promise<{ url: string; documentId: string; expiresAt: string }> {
    return this.request<{ url: string; documentId: string; expiresAt: string }>(
      "POST",
      `/api/jobs/${encodeURIComponent(jobId)}/contract/issue`,
      {},
    );
  }

  /**
   * `GET /api/agreement-links` — the public signing links this organisation
   * has published. Never the secret: the server keeps only its SHA-256.
   */
  async agreementLinks(): Promise<AgreementLinkView[]> {
    const res = await this.request<{ links: AgreementLinkView[] }>(
      "GET",
      "/api/agreement-links",
      {},
    );
    return res.links;
  }

  /**
   * `POST /api/agreement-links` — mints one. Owner or admin.
   *
   * Comes back WITH its URL, unlike every other credential here: this one is
   * meant to be published, so the composed address is the useful thing and
   * the raw secret is only part of it. Shown once either way.
   */
  async mintAgreementLink(
    label: string,
  ): Promise<{ secret: string; url: string | null; link: AgreementLinkView }> {
    return this.request<{ secret: string; url: string | null; link: AgreementLinkView }>(
      "POST",
      "/api/agreement-links",
      { body: { label } },
    );
  }

  /** `POST /api/agreement-links/:id/revoke` — withdraws one, for good. */
  async revokeAgreementLink(id: string): Promise<void> {
    await this.request<{ revoked: boolean }>(
      "POST",
      `/api/agreement-links/${encodeURIComponent(id)}/revoke`,
      {},
    );
  }

  /** `GET /api/org/agreement-link` — the link the field app offers customers. */
  async fieldAgreementLink(): Promise<string | null> {
    const res = await this.request<{ url: string | null }>("GET", "/api/org/agreement-link", {});
    return res.url;
  }

  /** `PUT /api/org/agreement-link` — sets it, or clears it with null. Owner or admin. */
  async setFieldAgreementLink(url: string | null): Promise<void> {
    await this.request<{ url: string | null }>("PUT", "/api/org/agreement-link", { body: { url } });
  }

  /**
   * `GET /api/handoff-tokens` — the hand-off credentials this organisation
   * holds. Never the secret: the server keeps only its SHA-256.
   */
  async handoffTokens(): Promise<HandoffTokenView[]> {
    const res = await this.request<{ tokens: HandoffTokenView[] }>(
      "GET",
      "/api/handoff-tokens",
      {},
    );
    return res.tokens;
  }

  /**
   * `POST /api/handoff-tokens` — mints one. Owner only.
   *
   * THE SECRET COMES BACK EXACTLY ONCE, in this response, and the server
   * cannot produce it again. Whatever shows it has to assume it will not get
   * a second chance.
   */
  async mintHandoffToken(label: string): Promise<{ secret: string; token: HandoffTokenView }> {
    return this.request<{ secret: string; token: HandoffTokenView }>(
      "POST",
      "/api/handoff-tokens",
      { body: { label } },
    );
  }

  /** `POST /api/handoff-tokens/:id/revoke` — withdraws one, for good. */
  async revokeHandoffToken(id: string): Promise<void> {
    await this.request<{ revoked: boolean }>(
      "POST",
      `/api/handoff-tokens/${encodeURIComponent(id)}/revoke`,
      {},
    );
  }

  /**
   * `GET /api/agreements/awaiting-countersignature` — what a customer has
   * signed and nobody here has yet.
   */
  async awaitingCountersignature(): Promise<AwaitingCountersignature[]> {
    const res = await this.request<{ waiting: AwaitingCountersignature[] }>(
      "GET",
      "/api/agreements/awaiting-countersignature",
      {},
    );
    return res.waiting;
  }

  /**
   * `POST /api/agreements/:envelopeId/countersign` — the company\x27s half of
   * the signature, given by the person pressing it.
   *
   * Answers 409 when it has already been countersigned, which is the ordinary
   * race of two people looking at the same list rather than a fault.
   */
  async countersign(
    envelopeId: string,
  ): Promise<{ documentId: string; countersignedAt: string; emailed: boolean }> {
    return this.request<{ documentId: string; countersignedAt: string; emailed: boolean }>(
      "POST",
      `/api/agreements/${encodeURIComponent(envelopeId)}/countersign`,
      {},
    );
  }

  /**
   * `GET /api/export-tokens` — the credentials that may read this
   * organisation's signed agreements. Never the secret.
   */
  async exportTokens(): Promise<ExportTokenView[]> {
    const res = await this.request<{ tokens: ExportTokenView[] }>(
      "GET",
      "/api/export-tokens",
      {},
    );
    return res.tokens;
  }

  /**
   * `POST /api/export-tokens` — mints one. Owner or admin.
   *
   * THE SECRET COMES BACK EXACTLY ONCE, in this response, and the server
   * cannot produce it again. Whatever shows it has to assume there will be no
   * second chance.
   */
  async mintExportToken(label: string): Promise<{ secret: string; token: ExportTokenView }> {
    return this.request<{ secret: string; token: ExportTokenView }>(
      "POST",
      "/api/export-tokens",
      { body: { label } },
    );
  }

  /** `POST /api/export-tokens/:id/revoke` — withdraws one, for good. */
  async revokeExportToken(id: string): Promise<void> {
    await this.request<{ revoked: boolean }>(
      "POST",
      `/api/export-tokens/${encodeURIComponent(id)}/revoke`,
      {},
    );
  }

  /**
   * `POST /api/jobs/:jobId/customer-agreement/issue` — the company's own
   * simple agreement, issued the same way the contract is.
   *
   * The SAME act as `issueContract` and deliberately the same shape: generate,
   * fix as evidence, raise the signature request, mint the link. It differs
   * only in which template runs, and therefore in what it refuses without --
   * this one needs the customer's name, email and address, and the wording the
   * company wrote once under Company. No price, no timetable, no cancellation
   * venue: those belong to the contract of sale and are not defaulted here.
   */
  async issueCustomerAgreement(
    jobId: string,
  ): Promise<{ url: string; documentId: string; expiresAt: string }> {
    return this.request<{ url: string; documentId: string; expiresAt: string }>(
      "POST",
      `/api/jobs/${encodeURIComponent(jobId)}/customer-agreement/issue`,
      {},
    );
  }

  /** `GET /api/jobs/:jobId/contract` — what was agreed, or null if nothing yet. */
  async jobContract(jobId: string): Promise<JobContract | null> {
    const res = await this.request<{ contract: JobContract | null }>(
      "GET",
      `/api/jobs/${encodeURIComponent(jobId)}/contract`,
      {},
    );
    return res.contract;
  }

  /**
   * `PUT /api/jobs/:jobId/contract` — creates or REPLACES it, lines and
   * payment stages included. Not a merge: the body is the whole agreement, so
   * a form that sent a subset would silently drop the rest.
   */
  async saveJobContract(jobId: string, input: unknown): Promise<JobContract | null> {
    const res = await this.request<{ contract: JobContract | null }>(
      "PUT",
      `/api/jobs/${encodeURIComponent(jobId)}/contract`,
      { body: input },
    );
    return res.contract;
  }

  /**
   * `POST /api/jobs/:jobId/documents/:type/generate` — produces a document.
   *
   * Comes back as a DRAFT, deliberately: the route does not issue on
   * generation, so somebody can read what was produced before fixing it as
   * evidence. A refusal names the fields the template would not render
   * without, which is the whole reason that error is worth showing verbatim.
   */
  async generateDocument(jobId: string, type: string): Promise<ListedDocument> {
    const res = await this.request<{ document: ListedDocument }>(
      "POST",
      `/api/jobs/${encodeURIComponent(jobId)}/documents/${encodeURIComponent(type)}/generate`,
      {},
    );
    return res.document;
  }

  /** `GET /api/organisation/trading-details` — the company's own details. */
  async organisationProfile(): Promise<OrganisationProfile> {
    const res = await this.request<{ tradingDetails: OrganisationProfile }>(
      "GET",
      "/api/organisation/trading-details",
      {},
    );
    return res.tradingDetails;
  }

  /**
   * `PUT /api/organisation/trading-details` — merges what is sent into what
   * is stored, leaving anything omitted alone, so a half-filled profile can be
   * finished later without wiping what is already there.
   */
  async saveOrganisationProfile(
    patch: OrganisationProfile,
  ): Promise<OrganisationProfile> {
    const res = await this.request<{ tradingDetails: OrganisationProfile }>(
      "PUT",
      "/api/organisation/trading-details",
      { body: patch },
    );
    return res.tradingDetails;
  }

  /** `GET /api/jobs/:jobId/sends` — what has already left, newest first. */
  async jobSends(jobId: string): Promise<DocumentSend[]> {
    const res = await this.request<{ sends: DocumentSend[] }>(
      "GET",
      `/api/jobs/${encodeURIComponent(jobId)}/sends`,
      {},
    );
    return res.sends;
  }

  // -------------------------------------------------------------- signatures

  createSignatureEnvelope(
    documentId: string,
    input: CreateEnvelopeInput,
  ): Promise<SignatureEnvelope> {
    return this.request<SignatureEnvelope>(
      "POST",
      `/api/documents/${encodeURIComponent(documentId)}/signature-envelopes`,
      { body: input },
    );
  }

  voidSignatureEnvelope(id: string): Promise<SignatureEnvelope> {
    return this.request<SignatureEnvelope>(
      "POST",
      `/api/signature-envelopes/${encodeURIComponent(id)}/void`,
      {},
    );
  }

  verifySignature(id: string): Promise<SignatureVerification> {
    return this.request<SignatureVerification>(
      "GET",
      `/api/signature-envelopes/${encodeURIComponent(id)}/verify`,
      {},
    );
  }

  // ------------------------------------------------------------ staff chat

  /**
   * Who is in this organisation.
   *
   * Gated on `chat: read` rather than on the administrative `member` grant, so
   * the roles that actually use chat can see who to talk to. Includes people
   * whose membership has ended, flagged inactive, so a message they wrote still
   * carries a name — `openDirectThread` still refuses to address one.
   */
  async colleagues(): Promise<Colleague[]> {
    const res = await this.request<{ colleagues: Colleague[] }>(
      "GET",
      "/api/chat/colleagues",
      {},
    );
    return res.colleagues;
  }

  /** `GET /api/chat/threads` — the caller's own list, with unread counts. */
  async chatThreads(): Promise<ChatThreadSummary[]> {
    const res = await this.request<{ threads: ChatThreadSummary[] }>(
      "GET",
      "/api/chat/threads",
      {},
    );
    return res.threads;
  }

  /** The single number for the navigation badge. */
  async chatUnread(): Promise<number> {
    const res = await this.request<{ total: number }>(
      "GET",
      "/api/chat/unread",
      {},
    );
    return res.total;
  }

  /**
   * Opens the direct thread with a colleague, creating it if this is the first
   * message. Idempotent, so calling it to *open* a conversation is safe: it
   * cannot produce a second empty thread beside the one already there.
   */
  openDirectThread(userId: string): Promise<ChatThread> {
    return this.request<ChatThread>("POST", "/api/chat/threads/direct", {
      body: { userId },
    });
  }

  getChatThread(id: string): Promise<ChatThread> {
    return this.request<ChatThread>(
      "GET",
      `/api/chat/threads/${encodeURIComponent(id)}`,
      {},
    );
  }

  /**
   * A page of a thread, oldest first.
   *
   * Reading does NOT move the read position — that is `markThreadRead`, and it
   * is separate precisely so a background fetch cannot swallow messages nobody
   * has looked at.
   */
  async threadMessages(
    id: string,
    opts: { limit?: number; beforeSeq?: number } = {},
  ): Promise<ChatMessage[]> {
    const res = await this.request<{ messages: ChatMessage[] }>(
      "GET",
      `/api/chat/threads/${encodeURIComponent(id)}/messages`,
      { query: { limit: opts.limit, beforeSeq: opts.beforeSeq } },
    );
    return res.messages;
  }

  postChatMessage(id: string, input: PostMessageInput): Promise<ChatMessage> {
    return this.request<ChatMessage>(
      "POST",
      `/api/chat/threads/${encodeURIComponent(id)}/messages`,
      {
        body: {
          body: input.body,
          ...(input.attachments && input.attachments.length > 0
            ? {
                attachments: input.attachments.map((a) => ({
                  kind: "document",
                  documentId: a.documentId,
                })),
              }
            : {}),
        },
      },
    );
  }

  /**
   * Moves the caller's read position, never backwards.
   *
   * `uptoSeq` is not optional here even though the route allows it to be. The
   * position is a watermark: it cannot say "read message 3 but not message 1",
   * so marking to the end of a thread the reader has only partly seen would
   * silently swallow the rest, with no way to get it back. Every caller in this
   * portal passes the highest sequence number it actually put on screen.
   */
  markThreadRead(id: string, uptoSeq: number): Promise<ThreadReadResult> {
    return this.request<ThreadReadResult>(
      "POST",
      `/api/chat/threads/${encodeURIComponent(id)}/read`,
      { body: { uptoSeq } },
    );
  }

  /** `GET /api/jobs/:jobId/chat/threads` — the conversations about one job. */
  async jobChatThreads(jobId: string): Promise<ChatThread[]> {
    const res = await this.request<{ threads: ChatThread[] }>(
      "GET",
      `/api/jobs/${encodeURIComponent(jobId)}/chat/threads`,
      {},
    );
    return res.threads;
  }

  createJobChatThread(jobId: string, subject: string): Promise<ChatThread> {
    return this.request<ChatThread>(
      "POST",
      `/api/jobs/${encodeURIComponent(jobId)}/chat/threads`,
      { body: { subject } },
    );
  }

  /**
   * Pulls a colleague into a job thread so it appears in their own list of
   * conversations.
   *
   * Grants them nothing they could not already read — a job thread is
   * readable by whoever can read the job — it only decides whose sidebar it
   * shows up in. Idempotent: adding somebody who is already a participant
   * changes nothing, which matters here because there is no route that lists
   * who already is one, so this client cannot avoid offering it.
   */
  addChatParticipant(
    threadId: string,
    userId: string,
  ): Promise<{ threadId: string; userId: string }> {
    return this.request<{ threadId: string; userId: string }>(
      "POST",
      `/api/chat/threads/${encodeURIComponent(threadId)}/participants`,
      { body: { userId } },
    );
  }

  /**
   * Turns a message into a tombstone.
   *
   * There is deliberately no `deleteChatMessage`. DELETE on `chat_messages` is
   * revoked from the application role at the database level, so a method that
   * promised removal could not keep the promise, and a screen offering it would
   * be offering a button that cannot work.
   */
  redactChatMessage(messageId: string, reason: string): Promise<ChatMessage> {
    return this.request<ChatMessage>(
      "POST",
      `/api/chat/messages/${encodeURIComponent(messageId)}/redact`,
      { body: { reason } },
    );
  }

  // -------------------------------------------------------- notifications

  /** `GET /api/notifications` — this person's feed. */
  async notifications(query: NotificationQuery = {}): Promise<Notification[]> {
    const res = await this.request<{ notifications: Notification[] }>(
      "GET",
      "/api/notifications",
      {
        query: {
          live: query.liveOnly === true ? "true" : undefined,
          unread: query.unreadOnly === true ? "true" : undefined,
          limit: query.limit,
        },
      },
    );
    return res.notifications;
  }

  notificationUnread(): Promise<UnreadSummary> {
    return this.request<UnreadSummary>("GET", "/api/notifications/unread", {});
  }

  /** Marks one notification read, for this person alone. Idempotent. */
  async markNotificationRead(id: string): Promise<boolean> {
    const res = await this.request<{ id: string; marked: boolean }>(
      "POST",
      `/api/notifications/${encodeURIComponent(id)}/read`,
      { body: {} },
    );
    return res.marked;
  }

  async markAllNotificationsRead(): Promise<number> {
    const res = await this.request<{ marked: number }>(
      "POST",
      "/api/notifications/read-all",
      { body: {} },
    );
    return res.marked;
  }

  // ----------------------------------------------- photographic evidence

  /**
   * `GET /api/surveys/:id/measurements` — the survey's tape measurements from
   * the Scannix app, in the order they were taken.
   *
   * Answers `null` rather than throwing on a 404: this page has already read
   * the survey, so a 404 here means the API it is talking to predates the
   * route (migration 0038), not that the survey is missing — the section then
   * says so instead of failing the page.
   */
  async surveyMeasurements(surveyId: string): Promise<SurveyMeasurement[] | null> {
    try {
      const res = await this.request<{ measurements: SurveyMeasurement[] }>(
        "GET",
        `/api/surveys/${encodeURIComponent(surveyId)}/measurements`,
        {},
      );
      return res.measurements;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  }

  // --------------------------------------------- survey share links (0041)

  /** `GET /api/surveys/:id/share-links` — every link ever made, newest first. */
  async surveyShareLinks(surveyId: string): Promise<SurveyShareLink[]> {
    const res = await this.request<{ links: SurveyShareLink[] }>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/share-links`,
      {},
    );
    return res.links;
  }

  /** `POST /api/surveys/:id/share-links` — the token comes back here and never again. */
  createSurveyShareLink(
    surveyId: string,
    input: NewSurveyShareLink,
  ): Promise<CreatedSurveyShareLink> {
    return this.request<CreatedSurveyShareLink>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/share-links`,
      { body: input },
    );
  }

  /** `POST /api/surveys/:id/share-links/:linkId/revoke` — at once, and for good. */
  async revokeSurveyShareLink(surveyId: string, linkId: string): Promise<SurveyShareLink> {
    const res = await this.request<{ link: SurveyShareLink }>(
      "POST",
      `/api/surveys/${encodeURIComponent(surveyId)}/share-links/${encodeURIComponent(linkId)}/revoke`,
      {},
    );
    return res.link;
  }

  /** `GET /api/surveys/:id/share-links/:linkId/views` — the audit of one link, newest first. */
  async surveyShareLinkViews(surveyId: string, linkId: string): Promise<ShareLinkView[]> {
    const res = await this.request<{ views: ShareLinkView[] }>(
      "GET",
      `/api/surveys/${encodeURIComponent(surveyId)}/share-links/${encodeURIComponent(linkId)}/views`,
      {},
    );
    return res.views;
  }

  // ------------------------------------------------------------ jobs map

  /** `GET /api/jobs/map` — the organisation's jobs, placed; `mine` for an own-scoped role. */
  jobsMap(opts: { mine?: boolean } = {}): Promise<JobsMapResponse> {
    return this.request<JobsMapResponse>("GET", "/api/jobs/map", {
      query: { mine: opts.mine ? "true" : undefined },
    });
  }

  /**
   * `GET /api/installations` — jobs being worked on in the field.
   *
   * `mine` is required of a role scoped to its own work (an installer); for
   * anyone who can read the organisation's jobs it narrows the list instead.
   */
  installations(opts: { mine?: boolean } = {}): Promise<InstallationsResponse> {
    return this.request<InstallationsResponse>("GET", "/api/installations", {
      query: { mine: opts.mine ? "true" : undefined },
    });
  }

  /**
   * `GET /api/jobs/:jobId/photos` — every photograph on a job, oldest first,
   * or only an installer's visits with `{ visit: "installation" }`.
   */
  async jobPhotos(jobId: string, opts: { readonly visit?: "installation" } = {}): Promise<PhotoEvidence[]> {
    const query = opts.visit === "installation" ? "?visit=installation" : "";
    const res = await this.request<{ photos: PhotoEvidence[] }>(
      "GET",
      `/api/jobs/${encodeURIComponent(jobId)}/photos${query}`,
      {},
    );
    return res.photos;
  }

  /**
   * `GET /api/photos/:id/image` — one photograph's bytes, as an object URL
   * the caller can hand straight to an `<img src>`.
   *
   * This exists as a fetch-then-blob rather than letting the browser load
   * the URL directly because an `<img>` tag cannot carry an
   * `Authorization: Bearer` header, and every route under `/api` requires
   * one. So the bytes come back through the same authenticated path as
   * every other call, and only then become a URL the DOM will accept.
   *
   * The caller OWNS the returned URL and must `URL.revokeObjectURL` it when
   * the image unmounts — an object URL pins its blob in memory for the life
   * of the document otherwise, and a survey with forty photographs would
   * hold forty full-resolution JPEGs alive across every navigation.
   *
   * Serves the stamped copy — the one with the date, time and position
   * burned into the pixels, which is what makes it evidence. Pass
   * `copy: "original"` for the un-stamped frame.
   */
  async photoImageObjectUrl(
    photoId: string,
    opts: { copy?: "stamped" | "original" } = {},
  ): Promise<string> {
    const query = opts.copy === "original" ? "?copy=original" : "";
    const response = await this.sendRaw(
      "GET",
      `/api/photos/${encodeURIComponent(photoId)}/image${query}`,
      "image/jpeg",
    );
    if (!response.ok) throw await this.readError(response);
    return URL.createObjectURL(await response.blob());
  }

  /** `GET /api/jobs/:jobId/capture-sessions` — the visits, most recent first. */
  async jobCaptureSessions(jobId: string): Promise<CaptureSession[]> {
    const res = await this.request<{ sessions: CaptureSession[] }>(
      "GET",
      `/api/jobs/${encodeURIComponent(jobId)}/capture-sessions`,
      {},
    );
    return res.sessions;
  }

  /**
   * Re-hashes the stored bytes of both halves of a photograph and compares them
   * with what was recorded when it arrived.
   *
   * A failed check comes back 200 with a reason, like the document check: the
   * caller asked a question and got a true answer. Making the failure
   * unmistakable is the screen's job.
   */
  verifyPhoto(id: string): Promise<PhotoIntegrity> {
    return this.request<PhotoIntegrity>(
      "GET",
      `/api/photos/${encodeURIComponent(id)}/verify`,
      {},
    );
  }

  /**
   * Corrects what a photograph is captioned, after the fact.
   *
   * Never the measured fix, the bearing or the timestamps — the database
   * refuses a change to any of those — only the description a person reads.
   * `note` is an optional line for why, kept by the API in an audit event
   * alongside the old and new text; there is no route that reads that trail
   * back, so this browser cannot show it, only add to it.
   */
  amendPhotoDescription(
    id: string,
    description: string,
    note?: string,
  ): Promise<PhotoEvidence> {
    return this.request<PhotoEvidence>(
      "PATCH",
      `/api/photos/${encodeURIComponent(id)}/description`,
      { body: { description, ...(note ? { note } : {}) } },
    );
  }

  /**
   * Records that a person set this photograph's position by hand.
   *
   * One-way and permanent: the database refuses to change or remove an
   * override once recorded — see `overridePhotoPosition` in
   * `apps/api/src/capture/capture-writer.ts` — so there is no method here that
   * could undo one, and none should ever be added.
   */
  overridePhotoPosition(
    id: string,
    input: OverridePositionInput,
  ): Promise<PhotoEvidence> {
    return this.request<PhotoEvidence>(
      "POST",
      `/api/photos/${encodeURIComponent(id)}/override-position`,
      { body: input },
    );
  }

  /**
   * The PAS 2030:2023 §6.11 shot list for one measure category.
   *
   * Needs no job. `apps/api/src/http/app.ts` registers this route with no
   * `tenant(...)` call and no permission check beyond the app-wide requirement
   * to be signed in, so it answers the same way for every caller and every
   * organisation — the value is in asking it before the work is covered up,
   * which can be days before a job record even exists for it.
   */
  requiredPhotosForMeasure(
    category: MeasureCategory,
  ): Promise<RequiredPhotosForMeasure> {
    return this.request<RequiredPhotosForMeasure>(
      "GET",
      `/api/measures/${encodeURIComponent(category)}/required-photos`,
      {},
    );
  }

  // ---------------------------------------------- organisation members

  /**
   * `GET /api/organisation/members` — everybody in this organisation, with
   * their role and whether their membership is active.
   *
   * Gated on `member:read` server-side (owner and admin), so this method
   * throws `ApiError` with `code: "forbidden"` for every other role. The
   * screen decides whether to attempt the call at all by consulting the same
   * matrix with `can({ role, resource: "member", action: "read" })`.
   */
  async organisationMembers(): Promise<Member[]> {
    const res = await this.request<{ members: Member[] }>(
      "GET",
      "/api/organisation/members",
      {},
    );
    return res.members;
  }

  /**
   * There is deliberately no "add a member with a password" method.
   *
   * The route it called is gone: an administrator typing a colleague's first
   * password left the administrator holding a credential that authenticates as
   * that colleague, with nothing ever forcing it to change. Inviting them is
   * the replacement — `inviteMember` below.
   */

  /**
   * Changes a role and/or the active flag. `role` alone rotates somebody's
   * duties; `active: false` retires a login without destroying the audit
   * trail. Refused with 409 `last_owner` if the change would leave nobody
   * as an active owner.
   */
  updateOrganisationMember(
    userId: string,
    patch: UpdateMemberInput,
  ): Promise<Member> {
    return this.request<Member>(
      "PATCH",
      `/api/organisation/members/${encodeURIComponent(userId)}`,
      { body: patch },
    );
  }

  /**
   * Sets a fresh password for a member the caller may manage. Not the
   * end user's own change-password flow — that is a different act and needs
   * the current password. This is administrative housekeeping for a
   * forgotten one, gated on `member:update` server-side.
   */
  resetOrganisationMemberPassword(
    userId: string,
    newPassword: string,
  ): Promise<MemberPasswordReset> {
    return this.request<MemberPasswordReset>(
      "POST",
      `/api/organisation/members/${encodeURIComponent(userId)}/reset-password`,
      { body: { newPassword } },
    );
  }

  // ------------------------------------------------ organisation invitations

  /**
   * `GET /api/organisation/invitations` — the people who have been invited and
   * have not accepted yet, newest first.
   *
   * Only the open ones: an accepted invitation has become a member and is on
   * the roster instead, and a withdrawn one is a fact kept for the account
   * trail rather than something to keep showing. An EXPIRED invitation is
   * still listed, because that is the case needing attention.
   */
  async organisationInvitations(): Promise<Invitation[]> {
    const res = await this.request<{ invitations: Invitation[] }>(
      "GET",
      "/api/organisation/invitations",
      {},
    );
    return res.invitations;
  }

  /**
   * Invites somebody to join this organisation. The API emails them a
   * single-use link; the token never comes back here.
   *
   * Refused with 409 `email_in_use` when the address already names an account
   * — either a member here or somebody attached elsewhere; `details.reason`
   * says which — and with 503 `not_configured` when the deployment cannot send
   * email at all, which is refused BEFORE an invitation is written.
   *
   * Inviting an address that already has an open invitation is a resend with a
   * fresh token rather than a second live link.
   */
  inviteMember(input: InviteMemberInput): Promise<SentInvitation> {
    return this.request<SentInvitation>("POST", "/api/organisation/invitations", {
      body: input,
    });
  }

  /**
   * Sends the invitation again with a FRESH token, which kills the previous
   * link. Refused with 409 `invitation_not_open` once it has been accepted or
   * withdrawn; `details.state` says which.
   */
  resendInvitation(id: string): Promise<SentInvitation> {
    return this.request<SentInvitation>(
      "POST",
      `/api/organisation/invitations/${encodeURIComponent(id)}/resend`,
      {},
    );
  }

  /** Withdraws an invitation. Final — the link stops working immediately. */
  async revokeInvitation(id: string): Promise<Invitation> {
    const res = await this.request<{ invitation: Invitation }>(
      "POST",
      `/api/organisation/invitations/${encodeURIComponent(id)}/revoke`,
      {},
    );
    return res.invitation;
  }

  // ------------------------------------- accepting a link with no session yet
  //
  // Every method below is `anonymous`: the person holding the link has no
  // session, and in the invitation case no account at all — following the link
  // is what creates one. Routing them through the 401 handler would try to sign
  // out a session that does not exist.

  /** `GET /auth/invitations/:token` — who invited them, and to what. */
  openInvitation(token: string): Promise<OpenInvitation> {
    return this.request<OpenInvitation>(
      "GET",
      `/auth/invitations/${encodeURIComponent(token)}`,
      { anonymous: true },
    );
  }

  /**
   * Chooses the password and accepts. Answers with a session, so the new member
   * lands in the portal rather than at a sign-in form: they have just proved
   * they hold the mailbox and chosen the password themselves, which is strictly
   * more than signing in asks for.
   *
   * Refused with 400 `weak_password` (the API's own wording says what is wrong)
   * and 410 `link_unusable` for every dead link, whatever killed it.
   */
  acceptInvitation(token: string, password: string): Promise<LoginResult> {
    return this.request<LoginResult>(
      "POST",
      `/auth/invitations/${encodeURIComponent(token)}/accept`,
      { anonymous: true, body: { password } },
    );
  }

  /**
   * Asks for a password reset link.
   *
   * Always 202 with the same body whether or not the address names an account,
   * and the screen shows the API's `message` verbatim — a portal that composed
   * its own confirmation would be the place the two cases started to differ,
   * which is how an endpoint anybody can post to becomes a way to test whether
   * somebody has an account here.
   */
  requestPasswordReset(email: string): Promise<PasswordResetRequested> {
    return this.request<PasswordResetRequested>("POST", "/auth/password-resets", {
      anonymous: true,
      body: { email },
    });
  }

  /** `GET /auth/password-resets/:token` — whose account the link belongs to. */
  openPasswordReset(token: string): Promise<OpenPasswordReset> {
    return this.request<OpenPasswordReset>(
      "GET",
      `/auth/password-resets/${encodeURIComponent(token)}`,
      { anonymous: true },
    );
  }

  /**
   * Spends the link. Deliberately does NOT sign anybody in — the API ends every
   * session the person holds and sends them to prove the new password works.
   */
  completePasswordReset(
    token: string,
    password: string,
  ): Promise<CompletedPasswordReset> {
    return this.request<CompletedPasswordReset>(
      "POST",
      `/auth/password-resets/${encodeURIComponent(token)}/complete`,
      { anonymous: true, body: { password } },
    );
  }

  // --------------------------------------------------------------- plumbing

  private async request<T>(
    method: string,
    path: string,
    opts: {
      query?: Record<string, QueryValue>;
      body?: unknown;
      anonymous?: boolean;
    },
  ): Promise<T> {
    const response = await this.sendRaw(
      method,
      path + qs(opts.query),
      "application/json",
      opts,
    );

    if (response.ok) {
      // 204 has no body; nothing here returns one today, but parsing "" as JSON
      // would be a confusing way to find that out if something starts to.
      if (response.status === 204) return undefined as T;
      return (await this.readJson(response)) as T;
    }

    const error = await this.readError(response);
    if (error.status === 401) this.onUnauthorised?.(error);
    throw error;
  }

  /**
   * The network call underneath both `request` (which reads JSON) and
   * `documentContent` (which reads HTML). Building the request and turning a
   * dropped connection into `ApiUnreachableError` is identical either way; what
   * differs is only the `Accept` header and how the body is read back, which is
   * left to the caller.
   */
  private async sendRaw(
    method: string,
    pathWithQuery: string,
    accept: string,
    opts: { body?: unknown; anonymous?: boolean; raw?: { readonly data: Blob | Uint8Array; readonly contentType: string } } = {},
  ): Promise<Response> {
    const headers = new Headers({ accept });

    if (!opts.anonymous) {
      const credentials = this.credentials();
      if (credentials) {
        headers.set("authorization", `Bearer ${credentials.token}`);
        if (credentials.organisationId) {
          headers.set("x-organisation-id", credentials.organisationId);
        }
      }
    }

    const init: RequestInit = { method, headers };
    if (opts.raw !== undefined) {
      headers.set("content-type", opts.raw.contentType);
      init.body = opts.raw.data as BodyInit;
    } else if (opts.body !== undefined) {
      headers.set("content-type", "application/json");
      init.body = JSON.stringify(opts.body);
    }

    try {
      return await this.doFetch(this.baseUrl + pathWithQuery, init);
    } catch (cause) {
      // A dropped connection is not an API error and must not be dressed up as
      // one: the request may well have been applied at the far end.
      throw new ApiUnreachableError(
        "The portal could not reach the API. Check your connection and try again.",
        { cause },
      );
    }
  }

  private async readJson(response: Response): Promise<unknown> {
    const text = await response.text();
    try {
      return JSON.parse(text) as unknown;
    } catch (cause) {
      throw new ApiUnreachableError(
        `The API answered ${response.status} with something that was not JSON.`,
        { cause },
      );
    }
  }

  private async readError(response: Response): Promise<ApiError> {
    let body: unknown;
    try {
      body = JSON.parse(await response.text()) as unknown;
    } catch {
      body = null;
    }

    const shape = body as {
      error?: unknown;
      message?: unknown;
      details?: unknown;
    } | null;

    const code =
      typeof shape?.error === "string" ? shape.error : `http_${response.status}`;
    const message =
      typeof shape?.message === "string"
        ? shape.message
        : // A proxy or a crash can return a non-JSON error. Say which status it
          // was rather than pretending the API explained itself.
          `The API returned ${response.status} without an explanation.`;
    const details =
      typeof shape?.details === "object" && shape.details !== null
        ? (shape.details as Record<string, unknown>)
        : undefined;

    return new ApiError(response.status, code, message, details);
  }
}

function qs(query: Record<string, QueryValue> | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}
